import React from 'react';
import { Helmet } from 'react-helmet';
import { jsonLd, absoluteUrl, truncate } from '../utils/seoUtils';

const SITE_NAME = "E Media Forum";
const SITE_URL = "https://emediaforum.com";
const SITE_DEFAULT_IMAGE = "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=1200&h=630&fit=crop";
const SITE_TWITTER = "@emediaforum";                      // or ""
const SITE_LOGO = `${SITE_URL}/logo-512x512.png`;         // ensure this exists
const RSS_PATH = "/rss.xml";
const ORG_SAME_AS = [
  "https://www.instagram.com/emediaforum/",
];

function SEO({
  title = SITE_NAME,
  description = "",
  image = SITE_DEFAULT_IMAGE,
  url = SITE_URL,
  type = "website",                // 'website' | 'article'
  publishedTime,                   // ISO for articles
  modifiedTime,
  authorName,
  section,                         // category for articles
  tags = [],                       // array of strings
  imageAlt = title,
}) {
  const canonical = absoluteUrl(SITE_URL, url);
  const ogImg = absoluteUrl(SITE_URL, image);
  const metaTitle = type === 'article' ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — ${title}`;
  const metaDescription = truncate(description || "", 160);

  // JSON-LD Website
  const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: SITE_LOGO },
      sameAs: ORG_SAME_AS,
    },
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/search?q={query}`,
      "query-input": "required name=query",
    },
  };

  // JSON-LD Article (NewsArticle)
  const articleLd = type === 'article' ? {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: title,
    description: metaDescription || undefined,
    mainEntityOfPage: canonical,
    image: [ogImg],
    datePublished: publishedTime,
    dateModified: modifiedTime || publishedTime,
    author: authorName ? { "@type": "Person", name: authorName } : undefined,
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: SITE_LOGO },
    },
    articleSection: section,
    keywords: Array.isArray(tags) && tags.length ? tags.join(", ") : undefined,
  } : null;

  return (
    <Helmet>
      {/* Basic */}
      <title>{metaTitle}</title>
      <link rel="canonical" href={canonical} />
      {metaDescription && <meta name="description" content={metaDescription} />}
      <meta name="robots" content="index, follow" />

      {/* OpenGraph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      {metaDescription && <meta property="og:description" content={metaDescription} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImg} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      {SITE_TWITTER && <meta name="twitter:site" content={SITE_TWITTER} />}
      <meta name="twitter:title" content={title} />
      {metaDescription && <meta name="twitter:description" content={metaDescription} />}
      <meta name="twitter:image" content={ogImg} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {/* Article-specific OG */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && authorName && (
        <meta property="article:author" content={authorName} />
      )}
      {type === "article" && section && (
        <meta property="article:section" content={section} />
      )}
      {type === "article" &&
        Array.isArray(tags) &&
        tags.map((t) => <meta key={t} property="article:tag" content={t} />)}

      {/* RSS feed */}
      <link
        rel="alternate"
        type="application/rss+xml"
        title={`${SITE_NAME} RSS`}
        href={absoluteUrl(SITE_URL, RSS_PATH)}
      />

      {/* JSON-LD */}
      <script type="application/ld+json">{jsonLd(websiteLd)}</script>
      {articleLd && <script type="application/ld+json">{jsonLd(articleLd)}</script>}
    </Helmet>
  );
}

export default SEO;